import { PermissionApi, UserAdminLoginApi } from '../../../features/auth/models/auth-api.model';
import { isSuperAdministrator } from '../../auth/auth-session.service';

export interface NavItem {
  id: string;
  label: string;
  route: string | null;
  children: NavItem[];
}

const ROUTE_LABELS: Record<string, string> = {
  '/tickets/attendees': 'Usuarios por validar',
  '/tickets/validation': 'Escanear entradas',
  '/tickets/payment-vouchers': 'Comprobantes de pago',
  '/sales/vouchers': 'Comprobantes de tienda',
  '/tickets/used': 'Entradas validadas',
};

function routeLabel(route: string | null | undefined, fallback: string): string {
  return (route && ROUTE_LABELS[route]) ?? fallback;
}

export function toNavItems(menu: PermissionApi[]): NavItem[] {
  return menu
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((item) => ({
      id: String(item.permission_id),
      label: routeLabel(item.route, item.name),
      route: toNavigableRoute(item.route),
      children: toNavItems(item.children ?? []),
    }));
}

// El backend puede mandar vistas sin navegación propia (categorías):
// route null o "#" significa "no es un link".
export function toNavigableRoute(route: string | null | undefined): string | null {
  return route && route !== '#' ? route : null;
}

// Arbol de navegación real (sidebar y breadcrumb del header usan el mismo):
// agrega "Supervisar cuenta" para super administradores cuando el backend
// todavía no la manda como vista propia en el menú.
export function buildNavItems(menu: PermissionApi[], user: UserAdminLoginApi | null): NavItem[] {
  const items = toNavItems(menu);
  if (isSuperAdministrator(user) && !items.some((item) => item.route === '/supervision')) {
    items.push({ id: 'account-supervision', label: 'Supervisar cuenta', route: '/supervision', children: [] });
  }
  return items;
}

// Ancestros (ya mapeados a NavItem) desde el nivel más alto hasta el item que
// coincide con path, para el breadcrumb del header. Mismo criterio de
// sub-ruta que isRoutePermitted: un nodo hoja sin hijos también cubre sus
// pantallas de detalle (ej. "/accounts/roles/new" bajo "/accounts/roles").
export function breadcrumbTrail(items: NavItem[], path: string): NavItem[] {
  for (const item of items) {
    if (item.route === path) {
      return [item];
    }
    if (item.children.length) {
      const childTrail = breadcrumbTrail(item.children, path);
      if (childTrail.length) {
        return [item, ...childTrail];
      }
    } else if (item.route && path.startsWith(`${item.route}/`)) {
      return [item];
    }
  }
  return [];
}

// Un nodo con hijos solo habilita su ruta exacta: cada sub-ruta depende de
// que su vista hija venga en el menú ("/accounts" + "/accounts/users" no
// habilita "/accounts/roles"). Un nodo hoja sí habilita sus pantallas de
// detalle, que no existen como vistas en el backend (ej. "/store/5/10/2").
// Un menu vacio significa "rol sin permisos asignados": no otorga ninguna ruta.
export function isRoutePermitted(menu: PermissionApi[], path: string): boolean {
  return menu.some((item) => {
    const route = toNavigableRoute(item.route);
    const children = item.children ?? [];
    if (route === path) return true;
    if (route && !children.length) return path.startsWith(`${route}/`);
    return isRoutePermitted(children, path);
  });
}

// Permiso de edición de la vista cuya ruta coincide exacto con path. Si la
// vista no está en el menú se deniega: no se asume permiso por omisión. Una
// sesión guardada antes de que el front leyera can_edit no trae el campo y
// también queda en solo lectura hasta el próximo login. Es UX, no seguridad:
// el backend sigue siendo quien autoriza las escrituras.
export function canEditRoute(menu: PermissionApi[], path: string): boolean {
  for (const item of menu) {
    if (toNavigableRoute(item.route) === path) return item.can_edit === true;

    if (canEditRoute(item.children ?? [], path)) return true;
  }
  return false;
}

// Las secciones agrupadoras (ej. "/accounts") no tienen pantalla propia:
// su destino es la primera vista hija que el rol tenga en el menú.
export function firstPermittedChildRoute(
  menu: PermissionApi[],
  parentRoute: string,
): string | null {
  for (const item of menu) {
    const children = item.children ?? [];
    if (toNavigableRoute(item.route) === parentRoute) return firstPermittedRoute(children);

    const childRoute = firstPermittedChildRoute(children, parentRoute);
    if (childRoute) return childRoute;
  }
  return null;
}

// La respuesta de login es la fuente de verdad para los accesos del rol.
// Elegimos la primera ruta navegable respetando el orden configurado, sin
// depender de nombres o IDs de roles que pueden crecer en el backend.
export function firstPermittedRoute(menu: PermissionApi[]): string | null {
  const visit = (items: PermissionApi[]): string | null => {
    for (const item of items.slice().sort((a, b) => a.order - b.order)) {
      const route = toNavigableRoute(item.route);
      if (route) return route;

      const childRoute = visit(item.children ?? []);
      if (childRoute) return childRoute;
    }

    return null;
  };

  return visit(menu);
}
